import React, { useEffect, useState } from "react";
import { format, parseISO, startOfISOWeek } from "date-fns";
import { delete_ScheduleByDate, getWeekSchedule, post_BulkSchedule } from "../../services/Schedule";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { FaEyeSlash } from "react-icons/fa6";
import useAuth from "../../hook/useAuth";
import useConfirm from "../../hook/useConfirm";
import useGroup from "../../hook/useGroup";
import Cookies from "js-cookie";

const formatTime = (time: string) => {
  if (!time) return "No time";
  const [hour, minute] = time.split(":");
  const h = parseInt(hour, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const formattedHour = h % 12 || 12;
  return `${formattedHour}:${minute} ${ampm}`;
}

const toLocalDate = (dateStr: string) => {
  const d = parseISO(dateStr);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const CloneSchedule = () => {
   const token = Cookies.get("token-my-cdir");
  const { auth }: any = useAuth();
  const { selectedGroupId }: any = useGroup();
  const navigate = useNavigate();

  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [cloneToDate, setCloneToDate] = useState(new Date());
  const [schedules, setSchedules] = useState<any[]>([]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});

  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const [isNewRow, setIsNewRow] = useState(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const fetchSchedules = () => {
    const formattedDate = format(selectedDate, "yyyy-MM-dd");

    getWeekSchedule(formattedDate, selectedGroupId, token!.toString())
      .then((res) => {
        setSchedules(res || []);
      })
      .catch(console.error);
  }

  useEffect(() => {
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleViewToggle = (index: number) => {
    setViewIndex(viewIndex === index ? null : index);
  }

  const handleEdit = (item: any, index: number) => {
    setViewIndex(null);
    setEditingIndex(index);
    setEditData({ ...item });
  }

  const handleCancel = () => {
    const updated = [...schedules];

    if (isNewRow && editingIndex !== null) {
      updated.splice(editingIndex, 1);
      setSchedules(updated);
    }

    setEditingIndex(null);
    setEditData({});
    setErrors({});
    setIsNewRow(false);
  }

  const handleSave = () => {
    const newErrors: any = {};

    if (!editData.activityName) newErrors.activityName = "Required";
    if (!editData.date) newErrors.date = "Required";
    if (!editData.startTime) newErrors.startTime = "Required";
    if (!editData.endTime) newErrors.endTime = "Required";
    if (!editData.location) newErrors.location = "Required";

    setErrors(newErrors);

    // stop save if errors exist
    if (Object.keys(newErrors).length > 0) return;

    const updated = [...schedules];
    updated[editingIndex!] = editData;

    setSchedules(updated);

    setEditingIndex(null);
    setErrors({});
    setIsNewRow(false);
  }

  const handleDelete = (index: number) => {
    const updated = schedules.filter((_, i) => i !== index);
    setSchedules(updated);
  }

  useEffect(() => {
    if (editingIndex !== null) {
      const el = document.querySelector(
        "input[placeholder='Activity Name']"
      ) as HTMLInputElement;

      el?.focus();
    }
  }, [editingIndex]);

  const handleAddRow = () => {
    if (editingIndex !== null) return; // prevent stacking edits

    const newRow = {
      activityName: "",
      date: "",
      startTime: "",
      endTime: "",
      location: "",
      language: "",
      description: "",
    };

    const updated = [...schedules, newRow];

    setSchedules(updated);

    // auto open edit mode for new row
    setEditingIndex(updated.length - 1);
    setEditData(newRow);

    setViewIndex(null);
    setErrors({});

    setIsNewRow(true);
  }

  const checkIfWeekExists = async (date: Date) => {
    const formatted = format(date, "yyyy-MM-dd");

    // reuse your existing API if possible
    const res = await getWeekSchedule(formatted, selectedGroupId, token!.toString())
      .catch(err => console.error(err));

    return res && res.length > 0;
  }

  const handleClone = async () => {
    if (isProcessing) return;

    setIsProcessing(true);

    const currentWeek = startOfISOWeek(new Date());
    const isCurrentWeek = format(cloneToDate, "yyyy-MM-dd") === format(currentWeek, "yyyy-MM-dd");

    // check existing data
    const exists = await checkIfWeekExists(cloneToDate);
    if (exists) {
      const message = isCurrentWeek
        ? "Schedules already exist for this week. Update current week?"
        : "Schedules already exist for this week. Do you want to update?"

      setConfirmData({
        title: "Warning",
        summary: message,
        confirmBtn: "Ok",
        isCancelBtn: true,
        isConfirm: (cb: any) => {
          cb();
          setIsProcessing(true);
          submitForm();
        }
      });
      setConfirmPopup(true);
      setIsProcessing(false);

      return;
    } else {
      submitForm();
    }
  }

  const submitForm = async () => {
    const targetWeekStart = startOfISOWeek(cloneToDate);

    const formattedData = schedules.map((item) => {
      const { id, ...res } = item;

      const originalDate = toLocalDate(item.date);
      const originalWeekStart = startOfISOWeek(originalDate);

      // difference in days from original week start
      const diffDays = Math.floor(
        (originalDate.getTime() - originalWeekStart.getTime()) / (1000 * 60 * 60 * 24)
      );

      // apply same offset to target week
      const newDate = new Date(targetWeekStart);
      newDate.setDate(targetWeekStart.getDate() + diffDays);

      return {
        ...res,
        groupId: selectedGroupId,
        date: format(newDate, "yyyy-MM-dd"),
        dayOfWeek: newDate.getDay(),
      };
    });

    const formattedDate = format(cloneToDate, "yyyy-MM-dd");
    await delete_ScheduleByDate(formattedDate, selectedGroupId, auth?.id, token!.toString())
      .catch((err) => {
        setIsProcessing(false);
        console.error(err);
      });

    await post_BulkSchedule(formattedData, auth?.id, token!.toString())
      .then(() => {
        setIsProcessing(false);
        navigate(-1);
      })
      .catch((err) => {
        setIsProcessing(false);
        console.error(err);
      });
  }

  return (
    <div className="p-3 xm:p-4 md:p-6 bg-white rounded overflow-x-hidden">

      {/* HEADER */}
      <div className="flex flex-col xm:flex-row justify-between items-start xm:items-center mb-6 gap-2">
        <h2 className="text-lg xm:text-xl font-bold text-secondary">Schedules</h2>
      </div>

      {/* CLONE SECTION */}
      <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-5 gap-4">

        <div className="flex flex-col xm:flex-row gap-4 w-full">

          <div className="w-full xm:w-auto">
            <label className="block text-secondary font-semibold text-xs mb-2">
              Clone From
            </label>
            <input
              type="week"
              value={format(selectedDate, "yyyy-'W'II")}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  setSelectedDate(new Date());
                  return;
                }
                const [year, week] = value.split("-W");
                const date = new Date(Number(year), 0, 1 + (Number(week) - 1) * 7);
                setSelectedDate(startOfISOWeek(date));
              }}
              onBlur={(e) => {
                if (!e.target.value) setSelectedDate(new Date());
              }}
              className="border p-2 rounded w-full outline-secondary"
            />
          </div>

          <div className="w-full xm:w-auto">
            <label className="block text-secondary font-semibold text-xs mb-2">
              Clone To
            </label>
            <input
              type="week"
              value={format(cloneToDate, "yyyy-'W'II")}
              min={format(new Date(), "yyyy-'W'II")}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  setCloneToDate(new Date());
                  return;
                }
                const [year, week] = value.split("-W");
                const date = new Date(Number(year), 0, 1 + (Number(week) - 1) * 7);
                setCloneToDate(startOfISOWeek(date));
              }}
              onBlur={(e) => {
                if (!e.target.value) setCloneToDate(new Date());
              }}
              className="border p-2 rounded w-full outline-secondary"
            />
          </div>

        </div>

        <button
          onClick={handleClone}
          className="w-full xm:w-auto px-6 py-2 min-w-[150px] rounded text-white text-sm font-semibold bg-secondary hover:bg-custom-blue flex justify-center items-center"
          disabled={isProcessing}
        >
          {isProcessing ?
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="18px" fill="#fff" className="mr-2 inline animate-spin"
                viewBox="0 0 26.349 26.35">
                <circle cx="13.792" cy="3.082" r="3.082" data-original="#000000" />
                <circle cx="13.792" cy="24.501" r="1.849" data-original="#000000" />
                <circle cx="6.219" cy="6.218" r="2.774" data-original="#000000" />
                <circle cx="21.365" cy="21.363" r="1.541" data-original="#000000" />
                <circle cx="3.082" cy="13.792" r="2.465" data-original="#000000" />
                <circle cx="24.501" cy="13.791" r="1.232" data-original="#000000" />
                <path
                  d="M4.694 19.84a2.155 2.155 0 0 0 0 3.05 2.155 2.155 0 0 0 3.05 0 2.155 2.155 0 0 0 0-3.05 2.146 2.146 0 0 0-3.05 0z"
                  data-original="#000000" />
                <circle cx="21.364" cy="6.218" r=".924" data-original="#000000" />
              </svg>
              Processing...
            </>
            :
            "Clone"
          }
        </button>
      </div>

      {/* TABLE */}
      <div className="w-full overflow-x-auto">
        <table className="min-w-[800px] w-full border">
          <thead className="bg-gray-100 text-left whitespace-nowrap">
            <tr>
              <th className="p-3 text-xs xm:text-sm">Activity</th>
              <th className="p-3 text-xs xm:text-sm">Date</th>
              <th className="p-3 text-xs xm:text-sm">Start Time</th>
              <th className="p-3 text-xs xm:text-sm">End Time</th>
              <th className="p-3 text-xs xm:text-sm">Actions</th>
            </tr>
          </thead>

          <tbody>
            {schedules.length > 0 ? (
              schedules.map((item, index) => {
                const isEditing = editingIndex === index;

                return (
                  <React.Fragment key={index}>
                    <tr className={`border-t ${item.activityName === "" ? "bg-blue-50" : ""}`}>
                      <td className="p-3 text-xs xm:text-sm break-words">
                        {item?.activityName}
                      </td>

                      <td className="p-3 text-xs xm:text-sm">
                        {item?.date ? format(new Date(item.date), "MM-dd-yyyy") : ""}
                      </td>

                      <td className="p-3 text-xs xm:text-sm">
                        {formatTime(item.startTime)}
                      </td>

                      <td className="p-3 text-xs xm:text-sm">
                        {formatTime(item.endTime)}
                      </td>

                      <td className="p-3 flex flex-wrap gap-2">
                        <button onClick={() => handleViewToggle(index)} disabled={isEditing} className="p-2 rounded-full hover:bg-blue-100 text-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                          title={viewIndex === index ? "Hide" : "View"}
                        >
                          {viewIndex === index ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                        </button>

                        <button onClick={() => handleEdit(item, index)} disabled={isEditing} className="p-2 rounded-full hover:bg-green-100 text-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit"
                        >
                          <FaEdit size={14} />
                        </button>

                        <button onClick={() => handleDelete(index)} disabled={isEditing} className="p-2 rounded-full hover:bg-red-100 text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>

                    {viewIndex === index && (
                      <tr>
                        <td colSpan={5} className="p-4 bg-gray-50 text-xs xm:text-sm break-words">
                          <p><b>Location:</b> {item?.location}</p>
                          <p><b>Language:</b> {item?.language || "Not specified"}</p>
                          <p><b>Description:</b> {item?.description}</p>
                        </td>
                      </tr>
                    )}

                    {isEditing && (
                      <tr>
                        <td colSpan={5} className="p-4 bg-blue-50">
                          <div className="grid grid-cols-1 xm:grid-cols-2 gap-4">

                            <input
                              placeholder="Activity Name"
                              value={editData.activityName}
                              onChange={(e) =>
                                setEditData({ ...editData, activityName: e.target.value })
                              }
                              className="border p-2 rounded outline-secondary"
                            />

                            {errors.activityName && (
                              <p className="text-red-500 text-xs">Required</p>
                            )}

                            <input
                              type="date"
                              min={format(startOfISOWeek(cloneToDate), "yyyy-MM-dd")}
                              max={format(new Date(startOfISOWeek(cloneToDate).getTime() + 6 * 86400000), "yyyy-MM-dd")}
                              value={editData?.date ? format(parseISO(editData.date), "yyyy-MM-dd") : ""}
                              onChange={(e) =>
                                setEditData({ ...editData, date: e.target.value })
                              }
                              className="border p-2 rounded outline-secondary"
                            />

                            {errors.date && (
                              <p className="text-red-500 text-xs">Required</p>
                            )}

                            <input
                              type="time"
                              value={editData.startTime}
                              onChange={(e) =>
                                setEditData({ ...editData, startTime: e.target.value })
                              }
                              className="border p-2 rounded outline-secondary"
                            />

                            {errors.startTime && (
                              <p className="text-red-500 text-xs">Required</p>
                            )}

                            <input
                              type="time"
                              value={editData.endTime}
                              onChange={(e) =>
                                setEditData({ ...editData, endTime: e.target.value })
                              }
                              className="border p-2 rounded outline-secondary"
                            />

                            {errors.endTime && (
                              <p className="text-red-500 text-xs">Required</p>
                            )}


                            <input
                              placeholder="Location"
                              value={editData.location || ""}
                              onChange={(e) =>
                                setEditData({ ...editData, location: e.target.value })
                              }
                              className="border p-2 rounded outline-secondary"
                            />

                            {errors.location && (
                              <p className="text-red-500 text-xs">Required</p>
                            )}

                            <input
                              type="text"
                              placeholder="Language (Optional)"
                              className="w-full mt-1 p-2 border rounded outline-secondary"
                              value={editData.language || ""}
                              onChange={(e) =>
                                setEditData({ ...editData, language: e.target.value })
                              }
                            />



                            <textarea
                              placeholder="Description"
                              value={editData.description || ""}
                              onChange={(e) =>
                                setEditData({ ...editData, description: e.target.value })
                              }

                              className="border p-2 rounded w-full col-span-1 xm:col-span-2 outline-secondary"
                            />

                            <div className="col-span-1 xm:col-span-2 flex flex-col xm:flex-row gap-3">
                              <button type="button" onClick={handleCancel} className="w-full xm:w-auto px-6 py-2 bg-gray-200 rounded hover:bg-gray-300 active:bg-gray-200">
                                Cancel
                              </button>

                              <button onClick={handleSave} className="w-full xm:w-auto px-6 py-2 bg-secondary text-white rounded hover:bg-custom-blue disabled:bg-gray-400 disabled:cursor-not-allowed"
                                disabled={
                                  !editData.activityName ||
                                  !editData.date ||
                                  !editData.startTime ||
                                  !editData.endTime ||
                                  !editData.location
                                }
                              >
                                Save
                              </button>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="text-center p-4 text-gray-500">
                  No schedules found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <button
        onClick={handleAddRow}
        className="mt-4 w-full xm:w-auto px-4 py-2 bg-secondary hover:bg-custom-blue text-white rounded"
      >
        + Add New
      </button>
    </div>
  );
};

export default CloneSchedule;